import React from 'react';
import type { SortDescriptor } from '@react-types/shared';
import { createSortDescriptor } from '@/components/table';
import { DomainItem } from '@/types';

export const useDomainsSorting = (domains: DomainItem[]) => {
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  const sortedDomains = React.useMemo(() => {
    if (!sortDescriptor?.column) return domains;

    const copy = [...domains];
    const { column, direction } = sortDescriptor;

    const compareStrings = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

    copy.sort((a, b) => {
      let cmp = 0;

      switch (column) {
        case 'id':
          cmp = (a.id ?? 0) - (b.id ?? 0);
          break;
        case 'domain':
          cmp = compareStrings(a.domain, b.domain);
          break;
        case 'title':
          cmp = compareStrings(a.title || '', b.title || '');
          break;
        case 'status':
          cmp = compareStrings(a.status || '', b.status || '');
          break;
        case 'organization':
          cmp = compareStrings(a.organization?.name || '', b.organization?.name || '');
          break;
        case 'created_at': {
          const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
          cmp = aDate - bDate;
          break;
        }
        default:
          cmp = 0;
      }

      return direction === 'descending' ? -cmp : cmp;
    });

    return copy;
  }, [domains, sortDescriptor]);

  return { sortDescriptor, setSortDescriptor, sortedDomains };
};
